import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export type PaymentReturnSession = {
  paymentIntentId: string;
  statusCancelled: boolean;
  langFromReturn: string | null;
};

/**
 * Lee ?web_pi= & tenant_id desde la URL (retorno Stripe/PayPal/MP), limpia la query y expone sesión para el panel inline.
 */
export function usePaymentReturnSession(effectiveTenantId: string | undefined): {
  session: PaymentReturnSession | null;
  clearSession: () => void;
} {
  const [searchParams, setSearchParams] = useSearchParams();
  const [session, setSession] = useState<PaymentReturnSession | null>(null);

  const clearSession = useCallback(() => setSession(null), []);

  useEffect(() => {
    const webPi = searchParams.get('web_pi')?.trim();
    if (!webPi || !effectiveTenantId) return;
    const tq = (searchParams.get('tenant_id') || '').trim();
    if (tq && tq !== effectiveTenantId) return;

    const rawLang = searchParams.get('lang')?.trim();
    const langFromReturn =
      rawLang && /^[a-z]{2}(-[a-z]{2})?$/i.test(rawLang)
        ? rawLang.slice(0, 2).toLowerCase()
        : null;

    setSession({
      paymentIntentId: webPi,
      statusCancelled: searchParams.get('status') === 'cancelled',
      langFromReturn,
    });

    const next = new URLSearchParams(searchParams);
    next.delete('web_pi');
    next.delete('status');
    next.delete('lang');
    next.delete('session_id'); // Stripe embedded return_url replaces {CHECKOUT_SESSION_ID}
    setSearchParams(next, { replace: true });
  }, [searchParams, effectiveTenantId, setSearchParams]);

  return { session, clearSession };
}
